export interface PopoverPosition {
  left: number;
  top: number;
}

export interface PopoverPositionInput {
  anchor: Pick<DOMRect, 'left' | 'top' | 'right'>;
  popoverWidth: number;
  popoverHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  // Horizontal distance between the anchor's edge and the popover.
  anchorGap: number;
  // Minimum space kept between the popover and every viewport edge.
  viewportMargin: number;
}

// Positions a fixed-position popover beside an anchor rectangle: prefers the
// anchor's right side, flips to the left when the popover would overflow, and
// finally clamps within the viewport margins. Vertically aligns with the
// anchor's top, clamped the same way.
export function computePopoverPosition(input: PopoverPositionInput): PopoverPosition {
  const {
    anchor,
    popoverWidth,
    popoverHeight,
    viewportWidth,
    viewportHeight,
    anchorGap,
    viewportMargin
  } = input;

  let left = anchor.right + anchorGap;
  if (left + popoverWidth > viewportWidth - viewportMargin) {
    left = anchor.left - popoverWidth - anchorGap;
  }
  if (left < viewportMargin) {
    left = Math.max(
      viewportMargin,
      Math.min(anchor.left, viewportWidth - popoverWidth - viewportMargin)
    );
  }

  let top = anchor.top;
  if (top + popoverHeight > viewportHeight - viewportMargin) {
    top = Math.max(viewportMargin, viewportHeight - popoverHeight - viewportMargin);
  }
  if (top < viewportMargin) {
    top = viewportMargin;
  }

  return { left, top };
}

export interface DropdownPlacement {
  // Exactly one of top/bottom is set: top when the panel hangs below the
  // anchor, bottom when it has been flipped above it.
  top?: number;
  bottom?: number;
  maxHeight?: number;
}

export interface DropdownPlacementInput {
  anchor: Pick<DOMRect, 'top' | 'bottom'>;
  // The panel's natural content height, uncapped.
  panelHeight: number;
  viewportHeight: number;
  // Vertical distance between the anchor's edge and the panel.
  anchorGap: number;
  // Minimum space kept between the panel and the viewport edge it grows towards.
  viewportMargin: number;
  // A clamp tighter than this makes the panel useless, so the viewport margin
  // is given up before shrinking any further.
  minPanelHeight: number;
  // A height the panel never exceeds even with room to spare — a long list's
  // own cap. Placement reasons about the capped height, so a list taller than
  // its cap does not flip up when the capped panel fits below.
  maxPanelHeight?: number;
}

// Places a fixed-position dropdown panel above or below an anchor rectangle:
// flips above only when the panel does not fit below AND does fit above. When
// it fits neither way it opens in the roomier direction with its height capped,
// so the whole panel stays inside the viewport and its content scrolls instead.
export function computeDropdownPlacement(input: DropdownPlacementInput): DropdownPlacement {
  const {
    anchor,
    panelHeight,
    viewportHeight,
    anchorGap,
    viewportMargin,
    minPanelHeight,
    maxPanelHeight
  } = input;

  const spaceBelow = viewportHeight - anchor.bottom - anchorGap - viewportMargin;
  const spaceAbove = anchor.top - anchorGap - viewportMargin;
  const below = anchor.bottom + anchorGap;
  const above = viewportHeight - anchor.top + anchorGap;
  const renderedHeight = Math.min(panelHeight, maxPanelHeight ?? panelHeight);

  if (renderedHeight <= spaceBelow) return { top: below, maxHeight: maxPanelHeight };
  if (renderedHeight <= spaceAbove) return { bottom: above, maxHeight: maxPanelHeight };

  const isBelowRoomier = spaceBelow >= spaceAbove;
  const availableSpace = isBelowRoomier ? spaceBelow : spaceAbove;
  const clamped = Math.max(availableSpace, minPanelHeight);
  const maxHeight = Math.min(clamped, maxPanelHeight ?? clamped);
  return isBelowRoomier ? { top: below, maxHeight } : { bottom: above, maxHeight };
}
